import { useEffect, useMemo, useState, useCallback } from "react"
import { fetchCommitments } from "src/services/api/commitments"
import { useCommitmentsStore } from "src/store"
import { useDrawerStore } from "src/store/drawerStore"
import { useMessageStore } from "src/store/messageStore"
import { CLIENT_EVENT_ACCORDION_EXPAND_DISABLED } from "src/config/featureFlags"
import { useOpportunitiesStore, useCommitmentAccordeonStore, useClientOpportunitiesStore } from "src/store"
import { useCommitmentEventsAccordionStore } from "src/store/commitmentEventsAccordionStore"
import { Filter } from "src/lib/components/basics/TabFilters/types"
import intl from "src/internationalization/intl"
import { isOpportunityActive } from "src/lib/utils/opportunityDates"

export const useCommitmentsSection = () => {
    const { commitmentsData, setCommitmentsData, setUnresolvedLiquidityCount, unresolvedLiquidityCount, setReloadCommitments } = useCommitmentsStore()
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const actualContext = useMessageStore((s: any) => s.actualContext)
    const setJsonContext = useMessageStore((s: any) => s.setJsonContext)
    const setActualContext = useMessageStore((s: any) => s.setActualContext)
    const openDrawer = useDrawerStore((s: any) => s.openDrawer)

    // Filtros de liquidez
    const liquidityFilters: Filter[] = useMemo(() => [
        { label: intl('all'), value: 'all', id: 'todos' },
        { label: intl('resolved'), value: 'resolved', id: 'resolved' },
        { label: intl('unresolved'), value: 'unresolved', id: 'unresolved' },
    ], [])

    const [selectedLiquidityFilter, setSelectedLiquidityFilter] = useState<Filter>(liquidityFilters[0])

    // filtros de fluxo de liquidez
    const liquidityStreamFilters: Filter[] = useMemo(() => [
        { label: intl('all'), value: 'all', id: 'all' },
        { label: intl('vencimento'), value: 'vencimento', id: 'vencimento' },
        { label: intl('caixa'), value: 'caixa', id: 'caixa' },
        { label: intl('oportunidade'), value: 'oportunidade', id: 'oportunidade' },
    ], [])

    const [selectedLiquidityStreamFilter, setSelectedLiquidityStreamFilter] = useState<Filter>(liquidityStreamFilters[0])

    const handleLiquidityStreamFilterChange = (filter: Filter) => {
        setSelectedLiquidityStreamFilter(filter)
    }

    const eventValueFilters: Filter[] = useMemo(() => [
        { label: intl('eventValueAll'), value: 'all', id: 'event-all' },
        { label: intl('eventValueLow'), value: 'low', id: 'event-low' },
        { label: intl('eventValueMid'), value: 'mid', id: 'event-mid' },
        { label: intl('eventValueHigh'), value: 'high', id: 'event-high' },
    ], [])

    const [selectedEventValueFilter, setSelectedEventValueFilter] = useState<Filter>(eventValueFilters[0])

    const handleEventValueFilterChange = (filter: Filter) => {
        setSelectedEventValueFilter(filter)
    }

    // Filtros de período (mantido para compatibilidade, mas não usado na filtragem ainda)
    const periodFilters: Filter[] = useMemo(() => [
        { label: intl('week'), value: 'week', id: 'week' },
        { label: intl('day'), value: 'day', id: 'day' },
    ], [])

    const [selectedPeriodFilter, setSelectedPeriodFilter] = useState<Filter>(periodFilters[0])

    // Calcula as datas baseado nos filtros selecionados
    const { beginDate, endDate } = useMemo(() => {
        const today = new Date()
        
        // Se o filtro de período for "day", usa a data atual para ambos
        if (selectedPeriodFilter.value === 'day') {
            return { beginDate: today, endDate: today }
        }
        
        // Caso contrário, usa o período padrão (próximos 10 dias)
        const start = new Date()
        start.setDate(today.getDate() + 10)
        return { beginDate: today, endDate: start }
    }, [selectedPeriodFilter])

    // Função para carregar os dados
    const loadCommitments = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const data = await fetchCommitments(beginDate, endDate)
            setCommitmentsData(data)
            const unresolvedCount = data?.commitments?.reduce((acc, commitment) => {
                const unresolved = commitment.clients.filter(
                    (client) => client.liquidityEventResolution === false
                ).length
                return acc + unresolved
            }, 0) ?? 0
            setUnresolvedLiquidityCount(unresolvedCount)
        } catch (err: any) {
            setError(err?.message ?? "Erro ao carregar compromissos")
            setCommitmentsData(undefined)
            setUnresolvedLiquidityCount(undefined)
        } finally {
            setLoading(false)
        }
    }, [beginDate, endDate, setCommitmentsData, setUnresolvedLiquidityCount])

    // Carrega os dados quando as datas mudam (incluindo quando o filtro muda)
    useEffect(() => {
        loadCommitments()
    }, [loadCommitments])

    // Registra a função de reload no store para ser chamada de outros componentes
    useEffect(() => {
        setReloadCommitments(loadCommitments)
        return () => {
            setReloadCommitments(undefined)
        }
    }, [loadCommitments, setReloadCommitments])

    const opportunitiesData = useOpportunitiesStore((s: any) => s.opportunitiesData)
    const setClientOpportunities = useClientOpportunitiesStore((s: any) => s.setClientOpportunities)
    
    // Lista base de commitments com oportunidades calculadas
    const baseCommitmentsList = useMemo(() => {
        if (!commitmentsData) return []

        const objectives = opportunitiesData?.objectives ?? []

        const countOpportunitiesForClient = (clientWrapper: any) => {
            try {
                const clientInfo = clientWrapper?.client
                if (!clientInfo) return 0
                return objectives.reduce((acc: number, objective: any) => {
                    const hasImpacted = objective?.impacted_clients?.some((impacted: any) =>
                        impacted?.p?.name === clientInfo?.name && impacted?.p?.account === clientInfo?.account
                    )
                    return acc + (hasImpacted ? 1 : 0)
                }, 0)
            } catch (error) {
                return 0
            }
        }

        return commitmentsData.commitments.map((commitment: any) => ({
            ...commitment,
            clients: commitment.clients.map((client: any) => ({
                ...client,
                opportunitiesCount: countOpportunitiesForClient(client),
            })),
        }))
    }, [commitmentsData, opportunitiesData])

    const clientOpportunities = useMemo(() => {
        if (!commitmentsData || !opportunitiesData) return []

        const objectives = opportunitiesData?.objectives ?? []
        const opportunitiesByClientKey = new Map<string, any[]>()

        const getClientKey = (name?: string, account?: string | number) => {
            if (!name || account === undefined || account === null) return ""
            return `${name}-${account}`
        }

        objectives.forEach((objective: any) => {
            if (!isOpportunityActive(objective)) return
            const impactedClients = objective?.impacted_clients ?? []
            impactedClients.forEach((impacted: any) => {
                const key = getClientKey(impacted?.p?.name, impacted?.p?.account)
                if (!key) return
                const list = opportunitiesByClientKey.get(key) ?? []
                const { impacted_clients, ...objectiveBase } = objective ?? {}
                opportunitiesByClientKey.set(key, [
                    ...list,
                    {
                        ...objectiveBase,
                        volumeOportunidade: impacted?.volumeOportunidade ?? 0,
                    },
                ])
            })
        })

        const clientsMap = new Map<string, any>()
        commitmentsData.commitments?.forEach((commitment: any) => {
            commitment.clients?.forEach((client: any) => {
                const key = getClientKey(client?.client?.name, client?.client?.account)
                if (!key || clientsMap.has(key)) return
                clientsMap.set(key, client)
            })
        })

        commitmentsData.customers_with_cash_to_invest?.forEach((item: any) => {
            const clientWrapper = {
                client: item?.cliente,
                net_amount: item?.amount,
                net_worth: item?.net_worth,
                last_updated_date: item?.most_recent_date,
                is_cash_opportunity: true,
            }
            const key = getClientKey(clientWrapper?.client?.name, clientWrapper?.client?.account)
            if (!key || clientsMap.has(key)) return
            clientsMap.set(key, clientWrapper)
        })

        return Array.from(clientsMap.entries()).map(([key, client]) => ({
            client,
            opportunities: opportunitiesByClientKey.get(key) ?? [],
        })).filter((item) => {
            if (item.opportunities.length === 0) return false
            const totalValue = item.opportunities.reduce((acc: number, opportunity: any) => {
                return acc + (opportunity?.volumeOportunidade ?? 0)
            }, 0)
            return totalValue > 0
        })
    }, [commitmentsData, opportunitiesData])

    useEffect(() => {
        setClientOpportunities(clientOpportunities)
    }, [clientOpportunities, setClientOpportunities])

    const filteredClientOpportunities = useMemo(() => {
        const streamValue = selectedLiquidityStreamFilter.value
        if (streamValue === 'vencimento' || streamValue === 'caixa') return []
        return clientOpportunities
    }, [clientOpportunities, selectedLiquidityStreamFilter])

    const streamFilteredCommitmentsList = useMemo(() => {
        const streamValue = selectedLiquidityStreamFilter.value
        if (streamValue === 'all') return baseCommitmentsList
        if (streamValue === 'caixa' || streamValue === 'oportunidade') return []

        return baseCommitmentsList.map((commitment: any) => ({
            ...commitment,
            clients: commitment.clients.filter(
                (client: any) => !!client.produto_a_vencer && !client.is_cash_opportunity
            ),
        })).filter((commitment: any) => commitment.clients.length > 0)
    }, [baseCommitmentsList, selectedLiquidityStreamFilter])

    const commitmentsList = useMemo(() => {
        const base = streamFilteredCommitmentsList

        if (selectedLiquidityFilter.value === 'resolved') {
            return base.map((commitment: any) => ({
                ...commitment,
                clients: commitment.clients.filter(
                    (client: any) => client.liquidityEventResolution === true
                ),
            })).filter((commitment: any) => commitment.clients.length > 0)
        }

        if (selectedLiquidityFilter.value === 'unresolved') {
            return base.map((commitment: any) => ({
                ...commitment,
                clients: commitment.clients.filter(
                    (client: any) => client.liquidityEventResolution === false
                ),
            })).filter((commitment: any) => commitment.clients.length > 0)
        }

        return base
    }, [streamFilteredCommitmentsList, selectedLiquidityFilter])

    // Lista dos usuarios com CASH
    const clientsWithCash = useMemo(() => {
        return commitmentsData?.customers_with_cash_to_invest ?? []
    }, [commitmentsData])

    const cashCommitmentsList = useMemo(() => {
        const streamValue = selectedLiquidityStreamFilter.value
        if (streamValue !== 'all' && streamValue !== 'caixa') return []

        const cashClients = (clientsWithCash ?? []).map((item: any) => ({
          client: {
            name: item?.cliente?.name,
            account: item?.cliente?.account,
            perfil: item?.cliente?.perfil, // pode vir undefined
          },
          net_amount: item?.amount,
          net_worth: item?.net_worth,
          last_updated_date: item?.most_recent_date,
          is_cash_opportunity: true,
        
        //   esses atributos tao indo so para nao quebrar o accordion
          liquidityEventResolution: false,
          allocation_details: item?.allocation_details ?? null,
          produto_a_vencer: null,
          finantialInstitution: item?.finantialInstitution ?? "BTG",
          source: item?.source ?? "btg",
          is_demo: item?.is_demo ?? false,
          opportunitiesCount: 0,
        }));
      
        if (!cashClients.length) return [];

        const today = new Date();
        const dueDate = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;

        return [
          {
            due_date: dueDate,
            clients: cashClients,
          },
        ];
      }, [clientsWithCash, selectedLiquidityStreamFilter]);

      const finalCommitmentsList = useMemo(() => {
        const baseList = commitmentsList ?? [];

        const mapByDate = new Map(
          baseList.map((c: any) => [c.due_date, { ...c, clients: [...c.clients] }])
        );

        for (const cashCommitment of cashCommitmentsList ?? []) {
          const sameDateCommitment = mapByDate.get(cashCommitment.due_date);
          if (sameDateCommitment) {
            sameDateCommitment.clients.push(...(cashCommitment.clients ?? []));
          } else {
            mapByDate.set(cashCommitment.due_date, {
              ...cashCommitment,
              clients: [...(cashCommitment.clients ?? [])],
            });
          }
        }

        // quando filtrando por 'oportunidade' não ha clientes regulares/caixa,
        // mas ainda precisa de uma entrada com a data de hoje para o CommitmentAccordion.Details renderizar clientOpportunities
        if (selectedLiquidityStreamFilter.value === 'oportunidade' && filteredClientOpportunities.length > 0) {
          const today = new Date()
          const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
          if (!mapByDate.has(todayKey)) {
            mapByDate.set(todayKey, { due_date: todayKey, clients: [] })
          }
        }

        const sorted = Array.from(mapByDate.values()).sort(
          (a, b) => String(a.due_date).localeCompare(String(b.due_date))
        );

        const eventValue = selectedEventValueFilter.value
        if (eventValue === 'all') return sorted

        return sorted.map((commitment: any) => ({
            ...commitment,
            clients: commitment.clients.filter((client: any) => {
                const amount = client.net_amount ?? 0
                if (eventValue === 'low') return amount < 100_000
                if (eventValue === 'mid') return amount >= 100_000 && amount < 500_000
                if (eventValue === 'high') return amount >= 500_000
                return true
            }),
        })).filter((commitment: any) => commitment.clients.length > 0)
      }, [commitmentsList, cashCommitmentsList, selectedLiquidityStreamFilter, filteredClientOpportunities, selectedEventValueFilter]);
      

    // Precisamos nos inscrever no estado do store para que o componente re-renderize quando mudar
    // O Zustand só re-renderiza quando o valor retornado pelo seletor muda
    // Como isOpen é uma função (referência não muda), precisamos nos inscrever em accordions
    const accordions = useCommitmentAccordeonStore((s: any) => s.accordions)
    const isOpen = useCommitmentAccordeonStore((s: any) => s.isOpen)
    const toggleAccordion = useCommitmentAccordeonStore((s: any) => s.toggleAccordion)
    const openAccordion = useCommitmentAccordeonStore((s: any) => s.openAccordion)
    const closeAccordion = useCommitmentAccordeonStore((s: any) => s.closeAccordion)

    const expandAll = () => {
        if (CLIENT_EVENT_ACCORDION_EXPAND_DISABLED) return
        openAccordion("all")
    }

    const collapseAll = () => {
        closeAccordion("all")
    }

    // Store para accordions de eventos (commitments)
    const eventsAccordions = useCommitmentEventsAccordionStore((s: any) => s.accordions)
    const isEventOpen = useCommitmentEventsAccordionStore((s: any) => s.isOpen)
    const toggleEventAccordion = useCommitmentEventsAccordionStore((s: any) => s.toggleAccordion)
    const expandAllEvents = useCommitmentEventsAccordionStore((s: any) => s.expandAll)
    const collapseAllEvents = useCommitmentEventsAccordionStore((s: any) => s.collapseAll)

    const handleClickAIButton = async () => {
        if (actualContext !== 'commitments') {
            try {
                setActualContext('commitments')
                setJsonContext(commitmentsData)
            } catch (error) {
                console.error("Error setting commitments context:", error)
            }
        }
        openDrawer()
    }

    // Calcula o total de eventos (número total de clientes em todos os commitments)
    const totalEvents = useMemo(() => {
        return commitmentsList.reduce((acc, commitment) => {
            return acc + (commitment.clients?.length || 0)
        }, 0)
    }, [commitmentsList])

    // Calcula o total de oportunidades (soma de opportunitiesCount de todos os clientes)
    const totalOpportunities = useMemo(() => {
        return clientOpportunities.reduce((acc, item) => acc + (item.opportunities?.length || 0), 0)
    }, [clientOpportunities])

    // Calcula a quantidade de clientes únicos na lista de commitmentsList
    const uniqueClientsCount = useMemo(() => {
        const uniqueClients = new Set<string>()
        commitmentsList.forEach((commitment) => {
            commitment.clients?.forEach((client: any) => {
                const clientInfo = client?.client
                if (clientInfo?.name && clientInfo?.account !== undefined) {
                    // Usa name + account como identificador único
                    const uniqueKey = `${clientInfo.name}-${clientInfo.account}`
                    uniqueClients.add(uniqueKey)
                }
            })
        })
        return uniqueClients.size
    }, [commitmentsList])

    // Calcula a quantidade de eventos com liquidityEventResolution: true
    const resolvedLiquidityEventsCount = useMemo(() => {
        return commitmentsList.reduce((acc, commitment) => {
            const resolved = commitment.clients.filter(
                (client: any) => client.liquidityEventResolution === true
            ).length
            return acc + resolved
        }, 0)
    }, [commitmentsList])

    // Calcula a porcentagem de eventos resolvidos para o LinearProgress
    const resolvedLiquidityEventsPercentage = useMemo(() => {
        if (totalEvents === 0) return 0
        return (resolvedLiquidityEventsCount / totalEvents) * 100
    }, [totalEvents, resolvedLiquidityEventsCount])

    // Calcula o total de clientes com oportunidades/objetivos
    const clientsWithOpportunitiesCount = useMemo(() => {
        return commitmentsList.reduce((acc, commitment) => {
            const clientsWithOpps = commitment.clients.filter(
                (client: any) => (client.opportunitiesCount || 0) > 0
            ).length
            return acc + clientsWithOpps
        }, 0)
    }, [commitmentsList])

    // Calcula quantos clientes com oportunidades têm liquidityEventResolution: true
    const resolvedClientsWithOpportunitiesCount = useMemo(() => {
        return commitmentsList.reduce((acc, commitment) => {
            const resolved = commitment.clients.filter(
                (client: any) => (client.opportunitiesCount || 0) > 0 && client.liquidityEventResolution === true
            ).length
            return acc + resolved
        }, 0)
    }, [commitmentsList])

    // Calcula a porcentagem de clientes com oportunidades que têm liquidityEventResolution: true
    const resolvedClientsWithOpportunitiesPercentage = useMemo(() => {
        if (clientsWithOpportunitiesCount === 0) return 0
        return (resolvedClientsWithOpportunitiesCount / clientsWithOpportunitiesCount) * 100
    }, [clientsWithOpportunitiesCount, resolvedClientsWithOpportunitiesCount])

    // Clientes únicos em customers_with_cash_to_invest
    const uniqueCashToInvestClientsCount = useMemo(() => {
        const customers = commitmentsData?.customers_with_cash_to_invest
        if (!customers) return 0
        const unique = new Set<string>()
        customers.forEach((item: any) => {
            const c = item?.cliente
            if (c?.name != null && c?.account !== undefined) {
                unique.add(`${c.name}-${c.account}`)
            }
        })
        return unique.size
    }, [commitmentsData])

    // Handlers para mudança de filtros
    const handleLiquidityFilterChange = (filter: Filter) => {
        setSelectedLiquidityFilter(filter)
    }

    const handlePeriodFilterChange = (filter: Filter) => {
        setSelectedPeriodFilter(filter)
    }

    return {
        loading,
        error,
        commitmentsList,
        cashCommitmentsList,
        finalCommitmentsList,
        isOpen,
        toggleAccordion,
        expandAll,
        collapseAll,
        handleClickAIButton,
        // Retornamos accordions para garantir que o componente re-renderize quando mudar
        accordions,
        totalEvents,
        totalOpportunities,
        unresolvedLiquidityCount,
        uniqueClientsCount,
        resolvedLiquidityEventsCount,
        resolvedLiquidityEventsPercentage,
        clientsWithOpportunitiesCount,
        resolvedClientsWithOpportunitiesCount,
        resolvedClientsWithOpportunitiesPercentage,
        clientOpportunities,
        filteredClientOpportunities,
        uniqueCashToInvestClientsCount,
        // Filtros
        liquidityFilters,
        selectedLiquidityFilter,
        handleLiquidityFilterChange,
        liquidityStreamFilters,
        selectedLiquidityStreamFilter,
        handleLiquidityStreamFilterChange,
        eventValueFilters,
        selectedEventValueFilter,
        handleEventValueFilterChange,
        periodFilters,
        selectedPeriodFilter,
        handlePeriodFilterChange,
        // Novos accordions de eventos
        eventsAccordions,
        isEventOpen,
        toggleEventAccordion,
        expandAllEvents,
        collapseAllEvents,
    }
}


